import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Save, X, ArrowRight, Lock, RefreshCw, Search, AlertTriangle, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupplierAliasManager = ({ user, onClose }) => {
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ alias_name: '', canonical_name: '' });
  const [newAlias, setNewAlias] = useState({ alias_name: '', canonical_name: '' });
  const [uniqueSuppliers, setUniqueSuppliers] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [stats, setStats] = useState({ total_default: 0, total_custom: 0 });
  const [lastNormalization, setLastNormalization] = useState(null);

  const fetchAliases = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/supplier-aliases`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setAliases(response.data.aliases || []);
      setStats({
        total_default: response.data.total_default || 0,
        total_custom: response.data.total_custom || 0
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch aliases');
    } finally {
      setLoading(false);
    }
  };

  const fetchUniqueSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/admin/supplier-aliases/unique-suppliers`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setUniqueSuppliers(response.data.suppliers || []);
    } catch (error) {
      console.log('Could not fetch unique suppliers');
    }
  };

  useEffect(() => {
    fetchAliases();
    fetchUniqueSuppliers();
  }, []);

  // Bulk normalize all existing orders
  const handleBulkNormalize = async () => {
    if (!window.confirm('This will normalize all supplier names in existing orders. Continue?')) {
      return;
    }

    setNormalizing(true);
    try {
      const response = await axios.post(`${API}/admin/normalize-suppliers`, {}, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      
      const data = response.data;
      const totalUpdated = (data.updated_orders || 0) + (data.updated_completed_orders || 0) + (data.updated_stock_winlife || 0);
      
      setLastNormalization({
        orders: data.updated_orders || 0,
        completed: data.updated_completed_orders || 0,
        stock: data.updated_stock_winlife || 0,
        total: totalUpdated
      });

      if (totalUpdated > 0) {
        toast.success(`Normalized ${totalUpdated} records!`);
      } else {
        toast.info('All supplier names are already normalized');
      }
      
      // Refresh suppliers list
      fetchUniqueSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Normalization failed');
    } finally {
      setNormalizing(false);
    }
  };

  const handleAddAlias = async (e) => {
    e.preventDefault();
    if (!newAlias.alias_name.trim() || !newAlias.canonical_name.trim()) {
      toast.error('Both fields are required');
      return;
    }

    try {
      await axios.post(`${API}/admin/supplier-aliases`, newAlias, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Alias created successfully');
      setShowAddForm(false);
      setNewAlias({ alias_name: '', canonical_name: '' });
      fetchAliases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create alias');
    }
  };

  const handleUpdateAlias = async (aliasId) => {
    try {
      await axios.put(`${API}/admin/supplier-aliases/${aliasId}`, editData, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Alias updated successfully');
      setEditingId(null);
      fetchAliases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update alias');
    }
  };

  const handleDeleteAlias = async (aliasId) => {
    if (!window.confirm('Are you sure you want to delete this alias?')) return;

    try {
      await axios.delete(`${API}/admin/supplier-aliases/${aliasId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Alias deleted successfully');
      fetchAliases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete alias');
    }
  };

  const startEdit = (alias) => {
    setEditingId(alias.id);
    setEditData({
      alias_name: alias.alias_name,
      canonical_name: alias.canonical_name
    });
  };

  const filteredAliases = aliases.filter(alias => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      alias.alias_name?.toLowerCase().includes(search) ||
      alias.canonical_name?.toLowerCase().includes(search)
    );
  });

  const defaultAliases = filteredAliases.filter(a => a.is_default);
  const customAliases = filteredAliases.filter(a => !a.is_default);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-white">Supplier Alias Manager</h2>
            <p className="text-indigo-200 text-sm">
              Manage supplier name variations • {stats.total_default} default, {stats.total_custom} custom
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={fetchAliases}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3">
          <div className="flex items-start gap-2 text-amber-800 text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <strong>How it works:</strong> When you enter a supplier name that matches an "Alias", 
              it will automatically be converted to the "Canonical Name". 
              For example, typing "care max" will become "CAREMAX FORMULATION".
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search aliases..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Alias
          </Button>
          <Button
            onClick={handleBulkNormalize}
            variant="outline"
            className="text-amber-600 border-amber-300 hover:bg-amber-50"
            disabled={normalizing}
            data-testid="bulk-normalize-btn"
          >
            {normalizing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Normalizing...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Bulk Normalize
              </>
            )}
          </Button>
        </div>

        {/* Normalization Results */}
        {lastNormalization && (
          <div className="px-6 py-3 bg-green-50 border-b border-green-200">
            <div className="flex items-center justify-between">
              <div className="text-green-800 text-sm">
                <strong>Last Normalization Result:</strong>{' '}
                {lastNormalization.total === 0 ? (
                  'All records already normalized'
                ) : (
                  <>
                    Updated {lastNormalization.total} records 
                    ({lastNormalization.orders} orders, {lastNormalization.completed} completed, {lastNormalization.stock} stock)
                  </>
                )}
              </div>
              <button 
                onClick={() => setLastNormalization(null)}
                className="text-green-600 hover:text-green-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Add Form */}
        {showAddForm && (
          <div className="px-6 py-4 bg-indigo-50 border-b border-indigo-200">
            <form onSubmit={handleAddAlias} className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Alias (variation to match)
                </label>
                <Input
                  type="text"
                  placeholder="e.g., CARE MAX"
                  value={newAlias.alias_name}
                  onChange={(e) => setNewAlias({ ...newAlias, alias_name: e.target.value.toUpperCase() })}
                  className="uppercase"
                />
              </div>
              <div className="flex items-center text-slate-400 pt-6">
                <ArrowRight className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-[200px] relative">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Canonical Name (standard name)
                </label>
                <Input
                  type="text"
                  placeholder="e.g., CAREMAX FORMULATION"
                  value={newAlias.canonical_name}
                  onChange={(e) => {
                    setNewAlias({ ...newAlias, canonical_name: e.target.value.toUpperCase() });
                    setShowSuggestions(e.target.value.length >= 2);
                  }}
                  onFocus={() => setShowSuggestions(newAlias.canonical_name.length >= 2)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  className="uppercase"
                />
                {/* Suggestions dropdown */}
                {showSuggestions && uniqueSuppliers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                    {uniqueSuppliers
                      .filter(s => s.toLowerCase().includes(newAlias.canonical_name.toLowerCase()))
                      .slice(0, 8)
                      .map((supplier, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setNewAlias({ ...newAlias, canonical_name: supplier });
                            setShowSuggestions(false);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-indigo-50 text-sm border-b border-slate-100 last:border-0"
                        >
                          {supplier}
                        </button>
                      ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-6">
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Custom Aliases */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs">
                    {customAliases.length}
                  </span>
                  Custom Aliases
                  <span className="text-slate-400 font-normal text-xs">(editable)</span>
                </h3>
                {customAliases.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                    <p>No custom aliases yet.</p>
                    <p className="text-sm mt-1">Click "Add New Alias" to create one.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {customAliases.map((alias) => (
                      <div
                        key={alias.id}
                        className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-indigo-300 transition-colors"
                      >
                        {editingId === alias.id ? (
                          <>
                            <Input
                              value={editData.alias_name}
                              onChange={(e) => setEditData({ ...editData, alias_name: e.target.value.toUpperCase() })}
                              className="flex-1 uppercase"
                            />
                            <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <Input
                              value={editData.canonical_name}
                              onChange={(e) => setEditData({ ...editData, canonical_name: e.target.value.toUpperCase() })}
                              className="flex-1 uppercase"
                            />
                            <Button size="sm" onClick={() => handleUpdateAlias(alias.id)} className="bg-green-600 hover:bg-green-700">
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 font-medium text-slate-700">{alias.alias_name}</span>
                            <ArrowRight className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                            <span className="flex-1 font-semibold text-indigo-700">{alias.canonical_name}</span>
                            <Button size="sm" variant="ghost" onClick={() => startEdit(alias)} className="text-slate-500 hover:text-indigo-600">
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteAlias(alias.id)} className="text-slate-500 hover:text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Default Aliases */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded text-xs">
                    {defaultAliases.length}
                  </span>
                  Default Aliases
                  <Lock className="w-3 h-3 text-slate-400" />
                  <span className="text-slate-400 font-normal text-xs">(read-only)</span>
                </h3>
                <div className="space-y-2">
                  {defaultAliases.map((alias) => (
                    <div
                      key={alias.id}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200"
                    >
                      <span className="flex-1 font-medium text-slate-500">{alias.alias_name}</span>
                      <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="flex-1 font-semibold text-slate-600">{alias.canonical_name}</span>
                      <Lock className="w-4 h-4 text-slate-300" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
          <span className="text-sm text-slate-500">
            Total: {filteredAliases.length} aliases
          </span>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SupplierAliasManager;
